import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { blacklistSenderFromRFQ, deleteRFQ } from "../../../../api/client";
import { queryKeys } from "../../../../api/queryKeys";
import { toast } from "../../../../utils/notifications";
import type { ErrorLike } from "../../../../utils/errors";
import type { RFQ } from "../../../../types/rfq";
import type { RfqSelection } from "./useRfqSelection";

/** What a refused delete tells the user. */
function deleteErrorDescription(error: ErrorLike): string {
  const errorMsg = error.message || '';
  const statusCode = error.response?.status || '';
  const serverError = error.response?.data?.error || '';

  if (statusCode === 404) {
    return 'RFQ not found. It may have been already deleted.';
  }
  if (statusCode === 400 && typeof serverError === 'string' && serverError.includes('foreign key constraint')) {
    return 'Cannot delete this RFQ because it has related records.';
  }
  if (errorMsg.includes('network') || !navigator.onLine) {
    return 'Network connection issue. Please check your internet connection and try again.';
  }
  if (typeof statusCode === 'number' && statusCode >= 500) {
    return 'Server error. Our team has been notified and is working on a fix.';
  }
  // Generic error with details when available
  return serverError || errorMsg || 'Unknown error';
}

/**
 * The RFQ list's dialogs and what confirming each one does: delete one RFQ,
 * delete the selected ones (one request each), blacklist a sender, and view
 * an RFQ's attachments.
 */
export function useRfqDialogs({ rfqs, selection, userName }: { rfqs: RFQ[]; selection: RfqSelection; userName: string | undefined }) {
  const queryClient = useQueryClient();
  const [rfqToDelete, setRfqToDelete] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [massDeletionMode, setMassDeletionMode] = useState(false);
  const [rfqToBlacklist, setRfqToBlacklist] = useState<{ rfqId: number; email: string; name: string } | null>(null);
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
  const [attachmentViewerOpen, setAttachmentViewerOpen] = useState(false);
  const [attachmentViewerRfqId, setAttachmentViewerRfqId] = useState<number | null>(null);
  const [attachmentViewerRfqSubject, setAttachmentViewerRfqSubject] = useState<string | undefined>(undefined);

  const invalidateLists = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all }); // Invalidate all tabs
    queryClient.invalidateQueries({ queryKey: queryKeys.rfqCounts.all });
  };

  const deleteMutation = useMutation({
    mutationFn: (rfqId: number) => deleteRFQ(rfqId),
    onSuccess: () => {
      invalidateLists();
      toast({
        title: "Success",
        description: "RFQ deleted successfully",
        severity: "success"
      });
      setDeleteDialogOpen(false);
      setRfqToDelete(null);
    },
    onError: (error: ErrorLike) => {
      toast({
        title: "RFQ Deletion Failed",
        description: deleteErrorDescription(error),
        severity: "error"
      });
      console.error('Delete error:', error);
    }
  });

  const blacklistMutation = useMutation({
    mutationFn: async (rfqId: number) => {
      return await blacklistSenderFromRFQ(rfqId, userName || 'Unknown', 'Blacklisted from UI');
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: `Sender has been blacklisted. Future emails from ${rfqToBlacklist?.email} will be ignored.`,
        severity: "success"
      });
      setBlacklistDialogOpen(false);
      setRfqToBlacklist(null);
      invalidateLists();
    },
    onError: (error: ErrorLike) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to blacklist sender';
      toast({
        title: "Error",
        description: errorMessage,
        severity: "error"
      });
    }
  });

  const openDelete = (rfqId: number) => {
    setRfqToDelete(rfqId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (rfqToDelete) {
      deleteMutation.mutate(rfqToDelete);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setRfqToDelete(null);
    setMassDeletionMode(false);
  };

  const openMassDelete = () => {
    setMassDeletionMode(true);
    setDeleteDialogOpen(true);
  };

  const confirmMassDelete = async () => {
    const { selectedRfqs } = selection;
    if (selectedRfqs.length > 0) {
      try {
        // One request per RFQ, in selection order
        for (const rfqId of selectedRfqs) {
          await deleteMutation.mutateAsync(rfqId);
        }

        selection.clearSelection();
        setMassDeletionMode(false);

        toast({
          title: "Success",
          description: `Successfully deleted ${selectedRfqs.length} RFQs`,
          severity: "success",
        });
      } catch (error) {
        console.error('Mass delete error:', error);
        toast({
          title: "Error",
          description: "An error occurred while deleting RFQs",
          severity: "error",
        });
      }
    }
  };

  const openBlacklist = (rfqId: number, clientEmail: string, clientName: string) => {
    setRfqToBlacklist({ rfqId, email: clientEmail, name: clientName });
    setBlacklistDialogOpen(true);
  };

  const confirmBlacklist = () => {
    if (rfqToBlacklist) {
      blacklistMutation.mutate(rfqToBlacklist.rfqId);
    }
  };

  const cancelBlacklist = () => {
    setBlacklistDialogOpen(false);
    setRfqToBlacklist(null);
  };

  const openAttachments = (rfqId: number) => {
    const rfq = rfqs.find(r => r.rfq_id === rfqId);
    setAttachmentViewerRfqId(rfqId);
    setAttachmentViewerRfqSubject(rfq?.original_subject);
    setAttachmentViewerOpen(true);
  };

  const closeAttachments = () => {
    setAttachmentViewerOpen(false);
    setAttachmentViewerRfqId(null);
    setAttachmentViewerRfqSubject(undefined);
  };

  return {
    selectedCount: selection.selectedRfqs.length,
    deleteDialogOpen,
    massDeletionMode,
    deletePending: deleteMutation.isPending,
    openDelete,
    confirmDelete,
    cancelDelete,
    openMassDelete,
    confirmMassDelete,
    rfqToBlacklist,
    blacklistDialogOpen,
    blacklistPending: blacklistMutation.isPending,
    openBlacklist,
    confirmBlacklist,
    cancelBlacklist,
    attachmentViewerOpen,
    attachmentViewerRfqId,
    attachmentViewerRfqSubject,
    openAttachments,
    closeAttachments,
  };
}

export type RfqDialogsState = ReturnType<typeof useRfqDialogs>;
